import { speaksLine } from '../components/PatientLanguage/PatientLanguage';
import { recallById } from '../recall-campaigns/store';
import type { LanguageCode } from '../platform-languages';
import type { TriageItem } from './types';

/**
 * The recall call queue — **defined once** (amendment 24 §7).
 *
 * Three surfaces were each carrying their own figure for one queue: the recall
 * agent page said *7 calls waiting*, the Dashboard said *12 calls expire today*,
 * and Triage's *Calls to make* showed 4. They were three literals, so of course
 * they disagreed.
 *
 * These seven rows are the queue. Triage renders them, `recallCallQueue()`
 * counts them, the Dashboard's Recall row shows the ones expiring today, and the
 * banner sentence is built from the same rows — so there is nothing left to
 * contradict.
 *
 * A recall call **expires**: the step's deadline passes and the patient moves on
 * (amendment 20 §5). `expiresToday` is what the Dashboard reports; it is not a
 * priority, and nothing about it is red.
 */
export interface RecallCall {
  id: number;
  patient: string;
  /** The recall it came from, by id in the recall store — never a loose string. */
  recallId: string;
  subject: string;
  /** Why a person is calling rather than ELVA. */
  reason: string;
  /** Deadline is today and nobody has made it. */
  expiresToday: boolean;
  assigneeId: string | null;
  attempts: number;
  dob: string;
  phone: string;
  insurance: string;
  /** What a person needs before dialling (amendment 25 §5). */
  language?: LanguageCode;
}

const CALLS: RecallCall[] = [
  {
    id: 9,
    patient: 'Tom Bradley',
    recallId: 'hygiene-6',
    subject: 'Hygiene recall — three messages, no reply',
    reason: 'Text, email and an AI call all went unanswered.',
    expiresToday: true,
    assigneeId: null,
    attempts: 0,
    dob: '06/18/1971',
    phone: '(210) 555-0723',
    insurance: 'Aetna',
  },
  {
    id: 10,
    patient: 'Eleanor Hayes',
    recallId: 'hygiene-6',
    subject: 'Hygiene recall — asked to be called about a time',
    reason: 'Replied to the text asking someone to ring her.',
    expiresToday: true,
    assigneeId: 'u_maria',
    attempts: 1,
    dob: '11/02/1959',
    phone: '(210) 555-0188',
    insurance: 'Delta Dental',
  },
  {
    // Farsi-speaking, so the task says so before anybody picks up the phone. The
    // AI call was skipped for the same reason — ELVA cannot call in Farsi yet.
    id: 11,
    patient: 'Maryam Rezaei',
    recallId: 'hygiene-6',
    subject: 'Hygiene recall — ELVA can’t call in her language yet',
    reason: 'The text went in Farsi; the call was skipped.',
    expiresToday: false,
    assigneeId: null,
    attempts: 0,
    dob: '03/24/1990',
    phone: '(210) 555-0188',
    insurance: 'Cigna',
    language: 'fa',
  },
  {
    id: 12,
    patient: 'Nora Davis',
    recallId: 'hygiene-6',
    subject: 'Hygiene recall — one attempt, awaiting callback',
    reason: 'Left a message with her husband.',
    expiresToday: false,
    assigneeId: 'u_saito',
    attempts: 1,
    dob: '07/25/1963',
    phone: '(210) 555-0455',
    insurance: 'Cigna',
  },
  {
    id: 15,
    patient: 'Grace Kim',
    recallId: 'lapsed-18',
    subject: 'Lapsed patient — two years since her last visit',
    reason: 'Opened every email, never booked.',
    expiresToday: false,
    assigneeId: 'u_priya',
    attempts: 0,
    dob: '09/14/1982',
    phone: '(210) 555-0611',
    insurance: 'Guardian',
  },
  {
    id: 16,
    patient: 'Kevin Wright',
    recallId: 'lapsed-18',
    subject: 'Lapsed patient — AI call reached him, he asked for a person',
    reason: 'Said on the call he had questions about the cost.',
    expiresToday: false,
    assigneeId: null,
    attempts: 0,
    dob: '01/30/1975',
    phone: '(210) 555-0742',
    insurance: 'None on file',
  },
  {
    id: 17,
    patient: 'Abdirahman Yusuf',
    recallId: 'lapsed-18',
    subject: 'Lapsed patient — last step of the recall',
    reason: 'The recall has run out of automated steps.',
    expiresToday: false,
    assigneeId: null,
    attempts: 2,
    dob: '05/08/1981',
    phone: '(210) 555-0369',
    insurance: 'Aetna',
  },
];

/** The recall's own name, from the store, so the two can never drift apart. */
function recallName(id: string): string {
  return recallById(id)?.name ?? id;
}

/** Built into Triage items, so Triage shows exactly what the counts count. */
export const RECALL_CALL_TASKS: TriageItem[] = CALLS.map((c) => ({
  id: c.id,
  patient: c.patient,
  itemKind: 'call_task',
  channel: 'none',
  triggeredBy: 'recall',
  goalType: c.recallId === 'lapsed-18' ? 'reactivate' : 'book',
  priority: 'medium',
  // A deadline, said as a deadline. Not "urgent".
  time: c.expiresToday ? 'Expires today' : 'Due this week',
  // `Call Maryam Rezaei · speaks Farsi` — the language belongs on the task, not
  // three clicks into the chart, because it changes who should make the call.
  subject: `${speaksLine(c.patient, c.language ?? 'en')} — ${c.subject}`,
  status: c.assigneeId === null ? 'open' : 'waiting',
  assigneeId: c.assigneeId,
  callAttempts: c.attempts,
  sourceLabel: recallName(c.recallId),
  sourceType: 'recall_call_task',
  callTask: {
    recallName: recallName(c.recallId),
    script: `Hi ${c.patient.split(' ')[0] ?? ''}, this is [your name] at Alamo Heights Dental. ${c.reason} I wanted to catch you myself and see if we can find a time that works.`,
    contextCard: [
      { label: 'Recall', value: recallName(c.recallId) },
      { label: 'Why a person', value: c.reason },
      { label: 'Deadline', value: c.expiresToday ? 'Today' : 'This week' },
      { label: 'Attempts so far', value: String(c.attempts) },
    ],
  },
  notes: [],
  tags: [recallName(c.recallId)],
  patientInfo: { dob: c.dob, phone: c.phone, insurance: c.insurance },
}));

export interface RecallCallQueue {
  /** Everything a person still has to call. */
  total: number;
  /** Of those, the ones whose deadline is today. */
  expiringToday: number;
  /** `[{ name, count }]`, by recall, most first. */
  byRecall: { id: string; name: string; count: number }[];
  /** The banner sentence — one string, built from the rows. */
  sentence: string;
}

/** Everybody's one source for the queue (§7). */
export function recallCallQueue(): RecallCallQueue {
  // Every row here is still to be made — a call that has been made leaves the
  // queue rather than sitting in it closed, which is why there is no filter.
  const open = CALLS;
  const groups = new Map<string, number>();
  for (const c of open) groups.set(c.recallId, (groups.get(c.recallId) ?? 0) + 1);
  const byRecall = [...groups.entries()]
    .map(([id, count]) => ({ id, name: recallName(id), count }))
    .sort((a, b) => b.count - a.count);
  return {
    total: open.length,
    expiringToday: open.filter((c) => c.expiresToday).length,
    byRecall,
    sentence: `${byRecall.map((g) => `${g.name} (${String(g.count)})`).join(', ')} — waiting in Calls.`,
  };
}

/** The Dashboard's Recall row (§7) — 2 of the 7, and it says which 2 they are. */
export function recallCallsExpiringToday(): number {
  return recallCallQueue().expiringToday;
}
